<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { toast } from "svelte-sonner";
  import { Save, ArrowLeft, Loader2, Sparkles, Lock } from "lucide-svelte";
  import { appStore } from '$lib/stores/appStore';

  let { page: initialPage, site_config, page_translations, editing_locale, page_i18n } = $props();

  const t = page_i18n || {}; 
  const translations = page_translations || {};
  let currentLocale = $state(editing_locale || initialPage?.locale || site_config.default_locale);
  
  // 🌟 修复 1：使用 initialPage 初始化 jsonRaw，避免 $effect 乱踩
  let jsonRaw = $state(JSON.stringify({ ...initialPage, locale: currentLocale }, null, 2));
  let processing = $state(false);
  let isDirty = $state(false);

  // 🌟 修复 2：$effect 只负责设置 Title，不要在里面把 initialPage 重新赋给 jsonRaw！
  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });

  function loadDataForLocale(loc: string) {
    if (loc === currentLocale) return;
    if (isDirty && !confirm(t.btn_switch_dirty)) return;

    router.visit(window.location.pathname, {
      data: { edit_locale: loc },
      preserveState: false, // 切语言强制刷新 Props
      onSuccess: () => {
        currentLocale = loc;
        isDirty = false;
        toast.success(t.flash?.switch_locale?.replace("%{locale}", loc.toUpperCase()));
      }
    });
  }

  let error = $derived.by(() => {
    try { JSON.parse(jsonRaw); return null; } 
    catch (e) { return e.message; }
  });

  function handleSave() {
    if (error) return toast.error(t.validation?.json_error || "JSON Error");
    processing = true;

    const isNew = !initialPage?.id || initialPage.id === 'undefined';
    const method = isNew ? "post" : "put";
    const url = isNew ? "/pages" : `/pages/${initialPage.id}`;

    router[method](url, { 
      full_config: jsonRaw,
      edit_locale: currentLocale  // 🌟 修复 3：统一与后端 params[:edit_locale] 一致
    }, {
      preserveState: false, // 🌟 关键：告诉 Inertia 保存成功重定向后，抛弃旧 State，完全使用后端最新返回的数据重装组件
      onFinish: () => processing = false,
      onSuccess: () => {
        isDirty = false;
        toast.success(t.flash?.save_success || "保存成功");
      },
      onError: (errors) => {
        console.error("提交失败:", errors);
      }
    });
  }
</script>

<div class="h-screen flex flex-col bg-zinc-950 text-zinc-300 font-mono">
  <div class="h-14 border-b border-zinc-800 flex items-center justify-between px-6 bg-zinc-900/50">
    <div class="flex items-center gap-6">
      <button onclick={() => router.visit("/pages")} class="hover:text-white"><ArrowLeft class="size-4" /></button>
      
      <div class="flex items-center gap-1 bg-black/40 p-1 rounded-lg border border-zinc-800">
        {#each site_config.supported_locales as loc}
          <button 
            onclick={() => loadDataForLocale(loc)}
            class="px-3 py-1 rounded text-[10px] font-bold transition-all 
              {currentLocale === loc ? 'bg-blue-600 text-white' : 'text-zinc-600 hover:text-zinc-400'}"
          >
            {loc.toUpperCase()}
          </button>
        {/each}
      </div>

      {#if currentLocale !== site_config.default_locale}
        <div class="flex items-center gap-1.5 px-2 py-0.5 rounded border border-amber-900/50 bg-amber-950/30 text-amber-500 text-[10px]">
          <Lock class="size-3" /> {t.mode_locked?.replace("%{mode}", initialPage.mode)}
        </div>
      {/if}
    </div>

    <div class="flex items-center gap-4">
      <button onclick={() => { try { jsonRaw = JSON.stringify(JSON.parse(jsonRaw), null, 2); } catch(e){} }} class="text-xs hover:text-white flex items-center gap-1.5">
        <Sparkles class="size-3 text-blue-400" /> {t.beautify}
      </button>

      <button 
        onclick={handleSave} 
        disabled={processing || !!error || (!isDirty && initialPage.id)} 
        class="bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-white text-xs font-bold px-4 py-1.5 rounded flex items-center gap-2"
      >
        {#if processing}<Loader2 class="size-3 animate-spin" />{:else}<Save class="size-3" />{/if}
        {t.btn_save?.replace("%{locale}", currentLocale.toUpperCase())}
      </button>
    </div>
  </div>

  <div class="flex-1 overflow-hidden relative">
    {#if error}
      <div class="absolute bottom-4 right-8 z-10 bg-red-950/80 border border-red-500/50 text-red-200 text-[10px] px-3 py-1.5 rounded">
        JSON Error: {error}
      </div>
    {/if}

    <textarea
      bind:value={jsonRaw}
      oninput={() => isDirty = true}
      spellcheck="false"
      class="w-full h-full bg-transparent p-8 outline-none resize-none text-sm leading-relaxed text-blue-100/80 font-mono"
      placeholder={t.editor_placeholder}
    ></textarea>
  </div>
</div>